import { defineStore } from 'pinia';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, FormatedEnum } from 'src/core/types/enum';
import { ResponseUserEnums } from 'src/core/types/enum/user';

type UserEnumsStore = {
  enums: ResponseUserEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing user enums data.
 */
export const useUserEnumsStore = defineStore('userEnums', {
  state: () =>
    ({
      enums: {} as ResponseUserEnums,
      formatedEnums: [] as FormatedEnum[],
    } as UserEnumsStore),

  getters: {
    /**
     * Get the formatted value of an enum.
     * @param enumVal - The enum value to format.
     * @returns The formatted enum value.
     */
    getFormatedEnumValue: (state) => (enumVal: string) => {
      return (
        state.formatedEnums.find(
          (enumValue: FormatedEnum) => enumValue[enumVal]
        )?.[enumVal] || enumVal
      );
    },

    /**
     * Get the enum instance based on the enum value.
     * @param enumVal - The enum value to search for.
     * @returns The enum value if found, undefined otherwise.
     */
    getEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumType | undefined;
      Object.keys(state.enums).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums[key as keyof typeof state.enums]?.find(
          (enumValue) => enumValue.value === enumVal
        );
      });
      return foundEnum;
    },
  },

  actions: {
    /**
     * Get enums information.
     */
    async getEnums() {
      try {
        const apiStore = useApiStore();
        const responseEnums = await apiStore.service.get<ResponseUserEnums>(
          '/user/enums'
        );
        this.formatedEnums = [];
        this.enums = responseEnums.data;
        Object.keys(responseEnums.data)?.forEach((key: string) => {
          responseEnums.data[key as keyof ResponseUserEnums]?.forEach(
            (enumValue: EnumType) => {
              if (enumValue.value && enumValue.label) {
                this.formatedEnums.push({
                  [enumValue.value]: enumValue.label,
                });
              } else {
                this.formatedEnums.push({ [enumValue]: enumValue });
              }
            }
          );
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('Enums error', e);
      }
    },
  },
});
